import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, EMPTY, Observable, of} from 'rxjs';
import {retry, tap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {ApiId, DistributionAccount, DistributionAccountModel} from '../distribution-account/distribution-account.model';
import {
  XandrBuyer, XandrDataPriceCategoryBuyerCustomPrice,
  XandrPricingDataCategory,
  XandrSegment,
  XandrSegmentBilling,
  XandrSegmentBillingRequest
} from './xandr.types';
import {AccountConfigurationWrapper} from '../segment-multi-share/domain';

@Injectable({
  providedIn: 'root'
})
export class XandrService {
  public account = new DistributionAccountModel({} as DistributionAccountModel);
  public isDryMode!: boolean;
  publishers = new BehaviorSubject(new Map<number, XandrBuyer[]>());
  buyers = new BehaviorSubject(new Map<number, XandrBuyer[]>());
  private allPublishers: { [Keys in ApiId as string]: any } = {XANDR: [], XANDR_CRTT: []};
  private allBuyers: { [Keys in ApiId as string]: any } = {XANDR: [], XANDR_CRTT: []};

  constructor(private http: HttpClient) {
  }

  getSegment(account: DistributionAccount) {
    const url = `${this.getApiBase(account.apiId)}/segment-api/${account.foreignIdForCurrentSegment}`;
    return this.http.get<XandrSegment>(url).pipe(retry(2));
  }

  getAllBuyers(apiId: ApiId): Observable<XandrBuyer[]> {
    return this.http.get<XandrBuyer[]>(`${this.getApiBase(apiId)}/buyers-api`)
      .pipe(tap((allBuyers: XandrBuyer[]) => this.allBuyers[apiId] = allBuyers));
  }

  getAllPublishers(apiId: ApiId): Observable<XandrBuyer[]> {
    return this.http.get<XandrBuyer[]>(`${this.getApiBase(apiId)}/publishers-api`)
      .pipe(tap(allPublishers => this.allPublishers[apiId] = allPublishers));
  }

  getBuyerSegmentSharings(accountId: number,foreignSegmentId: string, apiId: ApiId): Observable<XandrBuyer[]> {
    if (this.isDryMode) {
      return of(this.buyers.getValue().get(accountId) || []);
    } else {
      const url = `${this.getApiBase(apiId)}/buyer-segment-sharing-api/${foreignSegmentId}`;
      return this.http.get<XandrBuyer[]>(url).pipe(retry(2));
    }
  }
  getPublisherSegmentSharings(accountId: number,foreignSegmentId: string, apiId: ApiId): Observable<XandrBuyer[]> {
    if (this.isDryMode) {
      return of(this.publishers.getValue().get(accountId) || []);
    } else {
      const url = `${this.getApiBase(apiId)}/publisher-segment-sharing-api/${foreignSegmentId}`;
      return this.http.get<XandrBuyer[]>(url).pipe(retry(2));
    }
  }

  getCustomBuyersPrices(apiId: ApiId): Observable<XandrDataPriceCategoryBuyerCustomPrice[]> {
    const url = `${this.getApiBase(apiId)}/data-price-category-buyer-custom-price`;
    return this.http.get<XandrDataPriceCategoryBuyerCustomPrice[]>(url).pipe(retry(2));
  }

  shareSegmentToBuyer(buyerId: number, segmentForeignId: number | string,
                      isPublisher: boolean,account: DistributionAccount, apiId: ApiId): Observable<any> {

    if (!apiId) {
      throw new Error('Missing APIID');
    }

    if (this.isDryMode) {
      const publisher = this[isPublisher ? 'allPublishers' : 'allBuyers'][apiId].find((p: XandrBuyer) => p.buyerId === buyerId);
      const targetList = this[isPublisher ? 'publishers' : 'buyers'];
      const targetListValue = targetList.getValue();
      targetListValue.set(account.id,[...targetListValue.get(account.id) || [],publisher]);
      targetList.next(targetListValue);
      return of(targetList);
    } else {
      const body = {
        buyerId,
        isPublisher
      };
      const url = `${this.getApiBase(apiId)}/buyer-segment-sharing-api/${segmentForeignId}`;
      return this.http.put(url, body);
    }

  }

  deleteSegmentFromBuyer(buyerId: number, segmentForeignId: number, isPublisher: boolean, account: DistributionAccount,
                         apiId: ApiId): Observable<any> {
    if (this.isDryMode) {
      const targetList = this[isPublisher ? 'publishers' : 'buyers'];
      const targetListValue = targetList.getValue();
      targetListValue.set(account.id,(targetListValue.get(account.id) || []).filter((p: XandrBuyer) => p.buyerId !== buyerId));
      targetList.next(targetListValue);
      return of(targetList);
    } else {
      const url = `${this.getApiBase(apiId)}/buyer-segment-sharing-api/${segmentForeignId}?buyerId=${buyerId}&isPublisher=${isPublisher}`;
      return this.http.delete(url);
    }

  }

  getSegmentBillingMappings(account: DistributionAccount) {
    const apiId = account.apiId ? account.apiId : ApiId.XANDR;
    const url = `${this.getApiBase(apiId)}/segment-billing-mappings-api/${account.foreignIdForCurrentSegment}`;

    return this.http.get<XandrSegmentBilling>(url).pipe(retry(2));
  }

  getBillingCategories(apiId: ApiId) {
    const url = `${this.getApiBase(apiId)}/billing-categories-api`;
    return this.http.get<XandrPricingDataCategory[]>(url).pipe(retry(2));
  }

  updateSegmentStatus(segmentForeignId: string, active: boolean, apiId: ApiId) {
    const url = `${this.getApiBase(apiId)}/segment-api/${segmentForeignId}/status`;
    return this.http.put(url, {active}).pipe(retry(2));
  }

  createSegmentBillingMapping(segmentForeignId: string, segmentBillingMappingId: number, segmentType: string,
                              publicSegment: boolean, isActive: boolean,
                              apiId: ApiId): Observable<unknown | HttpErrorResponse> {
    const content = {
      segmentId: segmentForeignId,
      billingCategoryId: segmentBillingMappingId,
      type: segmentType,
      active: isActive,
      isPublic: publicSegment
    };
    const url = `${this.getApiBase(apiId)}/segment-billing-mappings-api`;
    return this.http.post(url, content);
  }

  createSegmentBillingMappingFromSegmentBilling(segmentForeignId: string, segmentBillingMapping: XandrSegmentBillingRequest, apiId: ApiId) {
    const content = {
      segmentId: segmentForeignId,
      billingCategoryId: segmentBillingMapping.dataCategoryId,
      type: segmentBillingMapping.dataSegmentTypeId,
      active: segmentBillingMapping.active,
      isPublic: segmentBillingMapping.public
    };
    const url = `${this.getApiBase(apiId)}/segment-billing-mappings-api`;
    return this.http.post(url, content);
  }

  updateSegmentBillingMapping(segmentForeignId: string, segmentBillingMappingId: number, segmentType: string, apiId: ApiId):
    Observable<unknown | HttpErrorResponse> {
    const content = {
      segmentId: segmentForeignId,
      billingCategoryId: segmentBillingMappingId,
      type: segmentType
    };
    const url = `${this.getApiBase(apiId)}/segment-billing-mappings-api`;
    return this.http.put(url, content);
  }

  deleteSegmentBillingMapping(segmentForeignId: string, apiId: ApiId): Observable<unknown | HttpErrorResponse> {
    const url = `${this.getApiBase(apiId)}/segment-billing-mappings-api/${segmentForeignId}`;
    return this.http.delete(url);
  }

  getApiBase(apiId: ApiId): string {
    return apiId === ApiId.XANDR ? `${environment.apiSegments}/xandr` : `${environment.apiSegments}/xandr/crtt`;
  }
}
